import { useState } from "react";
import { ArrowLeft, Check, ChevronDown, ChevronUp, PlayCircle, X } from "lucide-react";
import { Link } from "react-router-dom";

import { formatPersianNumber as numberFormatter } from "../../utils/persianNumber";

// Setup guide shown at the top of the dashboard until every step is done.
// It always names ONE next step with a single button, so a new user knows
// exactly what to do first; the full list stays available but secondary.
export default function OnboardingChecklist({ onboarding }) {
  const [expanded, setExpanded] = useState(false);
  const [showTutorial, setShowTutorial] = useState(false);

  if (!onboarding?.available || onboarding.complete) return null;

  const percentage = Math.round((onboarding.completedCount / onboarding.totalCount) * 100);
  const nextStep = onboarding.steps.find((step) => !step.completed);
  const remaining = onboarding.totalCount - onboarding.completedCount;

  return (
    <section className="rounded-2xl border border-gray-200 bg-white" aria-labelledby="onboarding-title">
      <div className="flex flex-wrap items-center gap-x-6 gap-y-3 px-4 py-3.5 sm:px-5">
        <div className="min-w-[12rem] flex-1">
          <div className="flex items-baseline justify-between gap-3">
            <h2 id="onboarding-title" className="text-sm font-extrabold text-accent">راه‌اندازی رستوران</h2>
            <span className="text-xs font-semibold text-gray-500">
              {numberFormatter(onboarding.completedCount)} از {numberFormatter(onboarding.totalCount)} مرحله
            </span>
          </div>
          <div
            className="mt-2 h-1.5 overflow-hidden rounded-full bg-gray-100"
            role="progressbar"
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={percentage}
            aria-label={`${numberFormatter(percentage)} درصد تکمیل شده`}
          >
            <div className="h-full rounded-full bg-primary transition-[width]" style={{ width: `${percentage}%` }} />
          </div>
        </div>

        {nextStep && (
          <div className="flex flex-wrap items-center gap-3">
            <p className="text-sm text-gray-500">
              قدم بعدی: <strong className="font-bold text-accent">{nextStep.label}</strong>
            </p>
            <Link
              to={nextStep.route}
              className="inline-flex min-h-9 items-center gap-1.5 rounded-lg bg-primary px-3.5 text-sm font-bold text-white transition-colors hover:bg-[#e8731f] focus:outline-none focus:ring-4 focus:ring-primary/20"
            >
              شروع
              <ArrowLeft size={16} aria-hidden="true" />
            </Link>
          </div>
        )}

        <button
          type="button"
          onClick={() => setExpanded((current) => !current)}
          className="inline-flex min-h-9 items-center gap-1 rounded-lg px-2 text-xs font-bold text-gray-500 hover:bg-gray-50 hover:text-accent focus:outline-none focus:ring-4 focus:ring-gray-100"
          aria-expanded={expanded}
          aria-controls="onboarding-content"
        >
          {expanded ? "بستن" : `همه مراحل (${numberFormatter(remaining)} باقی‌مانده)`}
          {expanded ? <ChevronUp size={15} aria-hidden="true" /> : <ChevronDown size={15} aria-hidden="true" />}
        </button>
      </div>

      {expanded && (
        <div id="onboarding-content" className="border-t border-gray-100 px-4 py-3 sm:px-5">
          <ol className="grid gap-x-4 gap-y-1 sm:grid-cols-2 lg:grid-cols-4">
            {onboarding.steps.map((step, index) => (
              <li key={step.id}>
                <Link
                  to={step.route}
                  className={`flex min-h-9 items-center gap-2 rounded-lg px-2 text-sm transition-colors hover:bg-gray-50 focus:outline-none focus:ring-4 focus:ring-primary/10 ${
                    step.completed ? "text-gray-400" : "font-semibold text-accent"
                  }`}
                >
                  {step.completed ? (
                    <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-secondary_1 text-secondary">
                      <Check size={13} strokeWidth={3} aria-hidden="true" />
                    </span>
                  ) : (
                    <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full border border-gray-300 text-[11px] font-bold text-gray-500">
                      {numberFormatter(index + 1)}
                    </span>
                  )}
                  <span className={step.completed ? "line-through decoration-gray-300" : ""}>{step.label}</span>
                </Link>
              </li>
            ))}
          </ol>

          <button
            type="button"
            onClick={() => setShowTutorial((current) => !current)}
            className="mt-2 inline-flex min-h-8 items-center gap-1.5 rounded-lg px-2 text-xs font-bold text-primary hover:underline focus:outline-none focus:ring-4 focus:ring-primary/10"
            aria-expanded={showTutorial}
          >
            {showTutorial ? <X size={15} aria-hidden="true" /> : <PlayCircle size={15} aria-hidden="true" />}
            {showTutorial ? "بستن ویدیوی راهنما" : "ویدیوی راهنمای شروع"}
          </button>

          {showTutorial && (
            <div className="mt-2 max-w-2xl overflow-hidden rounded-xl border border-gray-200 bg-black">
              <video className="aspect-video w-full" controls preload="metadata">
                <source src="/videos/tutorial.mp4" type="video/mp4" />
              </video>
            </div>
          )}
        </div>
      )}
    </section>
  );
}
